import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminInput from '../components/AdminInput'

const AdminMapSettings: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [clusterDissolveZoom, setClusterDissolveZoom] = useState<string>('15')

  const fetchMapSettings = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await adminApi.getMapSettings()
      setClusterDissolveZoom(String(response.data.cluster_dissolve_zoom))
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('지도 설정을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchMapSettings()
  }, [navigate, fetchMapSettings])

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  const handleSave = async () => {
    const zoomNumber = Number(clusterDissolveZoom)
    if (!Number.isInteger(zoomNumber)) {
      toastUtil.error('클러스터 해제 줌 레벨은 정수로 입력해주세요')
      return
    }
    if (zoomNumber < 14 || zoomNumber > 21) {
      toastUtil.error('클러스터 해제 줌 레벨은 14에서 21 사이로 입력해주세요')
      return
    }

    setIsSaving(true)
    try {
      await adminApi.updateMapSettings({
        cluster_dissolve_zoom: zoomNumber,
      })
      toastUtil.success('지도 설정이 저장되었습니다')
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors?.cluster_dissolve_zoom?.[0] ||
          '지도 설정 저장 중 오류가 발생했습니다'
        toastUtil.error(errorMessage)
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px max-w-[700px]">
            <div className="flex items-center justify-between">
              <h2 className="text-style-title2 text-textColorText2">지도 설정</h2>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  로딩 중...
                </div>
              </div>
            ) : (
              <div className="mt-24px">
                <AdminInput
                  label="클러스터 해제 시작 줌 레벨"
                  type="number"
                  min={14}
                  max={21}
                  value={clusterDissolveZoom}
                  onChange={(e) => setClusterDissolveZoom(e.target.value)}
                  placeholder="14 ~ 21"
                  labelClassName="block text-style-labelL text-textColorText2 mb-8px"
                  className="py-12px px-16px text-style-body1"
                />

                <p className="mt-8px text-style-captionL text-textColorText2">
                  입력한 줌 레벨 이상에서 클러스터가 해제되고 개별 핀만 표시됩니다
                </p>

                <div className="mt-24px flex justify-end">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className={`
                      py-12px
                      px-24px
                      bg-4B3621-80%
                      text-white
                      rounded-8px
                      text-style-button2
                      hover:bg-4B3621-80%-70%
                      transition-colors
                      disabled:bg-4B3621-80%-20%
                      disabled:cursor-not-allowed
                    `}
                  >
                    {isSaving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default AdminMapSettings
